/*
 * Copyright (c) Nexign, JSC, 2019-2023.
 */
/**
 * Header selector component
 *
 * @author Yakov Gushchin
 * @author Evgenij Tyan
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @author Julia Ignatieva
 * @author ILya Babanov
 * @author Evgeniy Naumov
 * @version 1.26.0
 * @since 1.0.0
 */
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import type { KeyboardEvent } from 'react';

import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { createStructuredSelector } from 'reselect';

import { icons } from '@assets/index';
import GTMOperations from '@modules/analytics/GoogleTagManager/GTMOperations';
import { KeyboardKeyCodeEnum } from '@modules/app/appConstants';
import type ICommonTranslation from '@modules/internationalization/types/common';
import type ICorporateTranslation from '@modules/internationalization/types/corporate';
import {
    makeSelectAuthoritiesIsNewsServiceUser,
    makeSelectAuthoritiesIsServiceUser,
    makeSelectUsername,
    makeSelectUserTypeId,
} from '@pages/PageAuth/selectors/authSelectors';
import type { IRootReducer } from '@reducers/rootReducer';
import { RoutePath } from '@routes/constants/routesConstants';

import { BlockWithIcon, EllipseText } from '../../common';
import {
    AboutLinkStyled,
    AboutLinkTextStyled,
    HeaderContainerStyled,
    HeaderDropDownMenuStyled,
    HeaderNameStyled,
    HeaderPhoneStyled,
} from './styled/HeaderSelectorStyled';

interface IHeaderSelectorProps {
    translation: ICorporateTranslation & ICommonTranslation;
}

export type THeaderSelectorProps = IHeaderSelectorProps & IConnectedProps;

export const HeaderSelector = (props: THeaderSelectorProps) => {
    const { isServiceUser, isNewsServiceUser, translation, username, userTypeId } = props;

    const [isOpen, setOpen] = useState<boolean>(false);

    /*
     * -------------------------------------------
     *               Handlers
     * -------------------------------------------
     */

    const showDropDownMenu = useCallback(() => setOpen(true), []);
    const hideDropDownMenu = useCallback(() => setOpen(false), []);

    useEffect(() => {
        return () => hideDropDownMenu();
    }, [hideDropDownMenu]);

    const keyDownHandler = useCallback(
        (event: KeyboardEvent) => {
            const key = event.keyCode;
            if (key === KeyboardKeyCodeEnum.ENTER) {
                showDropDownMenu();
            } else if (key === KeyboardKeyCodeEnum.ESCAPE) {
                hideDropDownMenu();
            }
        },
        [hideDropDownMenu, showDropDownMenu],
    );

    const getUserTypeName = useCallback(() => {
        return translation.userTypes[userTypeId] ?? userTypeId.toString();
    }, [translation.userTypes, userTypeId]);

    /*
     * -------------------------------------------
     *            Render menu items
     * -------------------------------------------
     */

    const linkSettingProps = useMemo(() => ({
        className: 'header-selector__dropdown dropdown__item',
        onClick: () => GTMOperations.navigation.additionalButtonsClickOperation(translation.selector.settings),
        children: <AboutLinkTextStyled>{translation.selector.settings}</AboutLinkTextStyled>,
    }), [translation.selector.settings]);

    const renderLinkAbout = useCallback(
        () => (
            <AboutLinkStyled
                className="header-selector__dropdown dropdown__item"
                data-dropdown-menu-item="aboutSystem"
                href={translation.aboutLink}
                onClick={() => GTMOperations.navigation.additionalButtonsClickOperation(translation.selector.about)}
            >
                <AboutLinkTextStyled>{translation.selector.about}</AboutLinkTextStyled>
            </AboutLinkStyled>
        ),
        [translation],
    );

    const renderChangeUserLink = useCallback(() => {
        return (
            isServiceUser && (
                <Link
                    className="header-selector__dropdown dropdown__item"
                    data-dropdown-menu-item="settings"
                    to={RoutePath.serviceAccessChangeUser}
                >
                    <AboutLinkTextStyled>{translation.selector.changeUser}</AboutLinkTextStyled>
                </Link>
            )
        );
    }, [translation.selector.changeUser, isServiceUser]);

    const renderContactInfo = useCallback(
        () => (
            <Fragment>
                <HeaderPhoneStyled>
                    {!isNewsServiceUser && translation.usernamePrefix}
                    {username}
                </HeaderPhoneStyled>
                <HeaderNameStyled>
                    <EllipseText title={getUserTypeName()}>{getUserTypeName()}</EllipseText>
                </HeaderNameStyled>
            </Fragment>
        ),
        [getUserTypeName, isNewsServiceUser, translation, username],
    );

    const renderSettingsLink = useCallback(() => {
        return (
            !isNewsServiceUser && (
                <Link {...linkSettingProps} data-dropdown-menu-item="settings" to={RoutePath.settingsProfile} />
            )
        );
    }, [isNewsServiceUser, linkSettingProps]);

    /*
     * -------------------------------------------
     *               Render
     * -------------------------------------------
     */

    return (
        <HeaderContainerStyled
            className={isOpen ? 'open' : ''}
            role="menubar"
            onKeyDown={keyDownHandler}
            onMouseEnter={showDropDownMenu}
            onMouseLeave={hideDropDownMenu}
        >
            <BlockWithIcon
                className="header-selector__block"
                iconLeft={icons.profileIconMedium}
                iconLeftSize={32}
                iconRight={icons.arrowListDownMedium}
                iconRightSize={32}
            />
            {isOpen && (
                <HeaderDropDownMenuStyled role="menu">
                    <div className="header-selector__dropdown dropdown__item">{renderContactInfo()}</div>

                    {renderLinkAbout()}

                    {renderSettingsLink()}

                    {renderChangeUserLink()}

                    <a
                        className="header-selector__dropdown dropdown__item header-selector__exit_button"
                        data-dropdown-menu-item="logout"
                        href={RoutePath.logout}
                        onClick={() => {
                            GTMOperations.navigation.additionalButtonsClickOperation(translation.selector.exit);
                        }}
                    >
                        <AboutLinkTextStyled>{translation.selector.exit}</AboutLinkTextStyled>
                    </a>
                </HeaderDropDownMenuStyled>
            )}
        </HeaderContainerStyled>
    );
};

interface IConnectedProps {
    isServiceUser: boolean;
    isNewsServiceUser: boolean;
    username: string;
    userTypeId: number;
}

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    isServiceUser: makeSelectAuthoritiesIsServiceUser(),
    isNewsServiceUser: makeSelectAuthoritiesIsNewsServiceUser(),
    username: makeSelectUsername(),
    userTypeId: makeSelectUserTypeId(),
});

export default connect(mapStateToProps)(HeaderSelector);
